import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaFire, FaUsers, FaLaughSquint, FaBriefcase, FaArrowUp,
  FaChevronDown, FaShoppingCart
} from 'react-icons/fa';
import useProductActions from '../hooks/useProductActions';
import { COMMERCE_SOURCES } from '../services/analyticsService';
import productosService from '../services/productosService';

/* ─── paleta cómic ─── */
const C = {
  paper:    '#EBF8F2',
  teal:     '#1F9DA2',
  deep:     '#196665',
  ink:      '#1D1F1C',
  sand:     '#F4D081',
  gold:     '#D2AD66',
  mustard:  '#E5D926',
  shadow:   '#482A18',
  cyan:     '#1BDDE1',
};

/* ─── datos del juego ─── */
const GAME_COMPONENTS = [
  { icon: <FaBriefcase />, title: 'Mazo de Trabajo', value: '60 cartas', detail: 'Tareas, imprevistos y presión administrativa.' },
  { icon: <FaLaughSquint />, title: 'Mazo de Acción', value: '70 cartas', detail: 'Efectos para reducir, mover o aumentar estrés.' },
  { icon: <FaFire />, title: 'Tokens de Estrés', value: '24 tokens', detail: 'Representan el desgaste acumulado del empleado.' },
  { icon: <FaUsers />, title: 'Rango de partida', value: '3–8 empleados', detail: 'Partidas rápidas de 20 a 30 minutos.' },
];

const CARD_EXAMPLES = [
  { image: '/images/ahilesdejo/carta-1.png', title: 'Renuncio con efecto inmediato', desc: 'Carta de escape definitiva — descarta toda tu mano y sales del juego ganando', accent: C.sand },
  { image: '/images/ahilesdejo/carta-2.png', title: 'Eso pudo ser un correo', desc: 'Cancela cualquier junta convocada por otro jugador', accent: C.cyan },
  { image: '/images/ahilesdejo/carta-3.png', title: 'Somos una familia… tóxica', desc: 'Roba 1 punto de moral a TODOS los jugadores', accent: C.mustard },
  { image: '/images/ahilesdejo/carta-4.png', title: 'Pizza motivacional', desc: 'Recupera 2 puntos de moral — porque pizza arregla todo', accent: C.teal },
  { image: '/images/ahilesdejo/carta-5.png', title: 'KPI imposible', desc: 'El jugador objetivo pierde un turno preparando reportes', accent: C.gold },
  { image: '/images/ahilesdejo/carta-6.png', title: 'Feedback constructivo', desc: 'Mira la mano de un rival y roba su mejor carta', accent: C.sand },
];

const GAME_SECTIONS = [
  { id: 'concepto', label: 'El Juego' },
  { id: 'componentes', label: 'Componentes' },
  { id: 'cartas', label: 'Cartas' },
  { id: 'como-jugar', label: 'Cómo se Juega' },
  { id: 'comprar', label: 'Comprar' },
];

/* ─── estilos reutilizables ─── */
const comicBorder = `3px solid ${C.ink}`;
const comicShadow = `6px 6px 0px ${C.shadow}`;
const comicShadowSm = `4px 4px 0px ${C.shadow}`;
const HERO_BG_IMAGE = '/images/ahilesdejo/hero-title-bg.png';

/* ─── componente principal ─── */
const AHI_LES_DEJO_ID = 1239706;

const AhiLesDejoPage = () => {
  const [activeSection, setActiveSection] = useState('');
  const [gameProduct, setGameProduct] = useState(null);
  const { buyNow } = useProductActions(COMMERCE_SOURCES.AHILESDEJO);

  useEffect(() => {
    productosService.getProducto(AHI_LES_DEJO_ID)
      .then(p => setGameProduct(p))
      .catch((_err) => { /* producto no disponible */ });
  }, []);

  // Delegado en useProductActions: emite el evento buy_now con su origen y,
  // a diferencia de antes, NO vacía el carrito del usuario.
  const handleBuyNow = () => buyNow(gameProduct, 1);

  /* scroll spy */
  useEffect(() => {
    const handleScroll = () => {
      const sections = GAME_SECTIONS.map(s => ({
        id: s.id,
        el: document.getElementById(s.id),
      })).filter(s => s.el);

      for (let i = sections.length - 1; i >= 0; i--) {
        const rect = sections[i].el.getBoundingClientRect();
        if (rect.top <= 200) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: C.paper, color: C.ink }}>

      {/* ════════════ HERO ════════════ */}
      <section
        className="relative overflow-hidden pt-8 pb-20 md:pt-12 md:pb-32"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(31,157,162,0.88) 0%, rgba(25,102,101,0.9) 100%), url(${HERO_BG_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* decorative dots pattern */}
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(${C.ink} 1.5px, transparent 1.5px)`,
            backgroundSize: '20px 20px',
          }}
        />
        {/* glow */}
        <div
          className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full blur-3xl pointer-events-none"
          style={{ backgroundColor: C.cyan, opacity: 0.08 }}
        />

        <div className="container mx-auto px-4 relative z-10 text-center">
          <span
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-extrabold rounded-full mb-8"
            style={{ backgroundColor: C.mustard, color: C.ink, border: comicBorder, boxShadow: comicShadowSm }}
          >
            <FaFire className="animate-pulse" />
            ¡YA DISPONIBLE! — PRIMER JUEGO EDITORIAL
          </span>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold mb-6 leading-none tracking-tight">
            <span
              className="inline-block"
              style={{
                color: C.sand,
                textShadow: `4px 4px 0px ${C.shadow}, -1px -1px 0px ${C.ink}, 1px -1px 0px ${C.ink}, -1px 1px 0px ${C.ink}`,
              }}
            >
              Ahí les dejo
            </span>
          </h1>

          <span
            className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-extrabold mb-6"
            style={{ backgroundColor: C.paper, color: C.deep, border: comicBorder, boxShadow: comicShadowSm }}
          >
            130 cartas + 24 tokens de estrés
          </span>

          <p className="text-xl md:text-2xl max-w-2xl mx-auto mb-6 font-semibold" style={{ color: C.paper }}>
            El juego de cartas que tu jefe no quiere que conozcas.
          </p>
          <p className="text-lg max-w-xl mx-auto mb-10" style={{ color: `${C.paper}cc` }}>
            Sobrevive la toxicidad laboral, esquiva juntas inútiles y renuncia con estilo.
            130 cartas + 24 tokens de estrés en una oficina donde solo sobrevive uno.
          </p>

          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-12">
            {[
              { icon: <FaUsers />, value: '3 – 8', label: 'Empleados' },
              { icon: <FaLaughSquint />, value: '130', label: 'Cartas Totales' },
              { icon: <FaBriefcase />, value: '20 – 30', label: 'Minutos' },
              { icon: <FaFire />, value: '24', label: 'Tokens de Estrés' },
            ].map((s, i) => (
              <div
                key={i}
                className="flex items-center gap-3 rounded-xl px-5 py-3"
                style={{ backgroundColor: C.paper, border: comicBorder, boxShadow: comicShadowSm }}
              >
                <span className="text-xl" style={{ color: C.teal }}>{s.icon}</span>
                <div className="text-left">
                  <p className="font-extrabold text-lg leading-tight" style={{ color: C.ink }}>{s.value}</p>
                  <p className="text-xs" style={{ color: C.deep }}>{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={handleBuyNow}
            className="inline-flex items-center gap-2 font-extrabold py-4 px-10 rounded-xl text-lg transition-all transform hover:scale-105 hover:translate-y-[-2px]"
            style={{
              backgroundColor: C.sand,
              color: C.ink,
              border: comicBorder,
              boxShadow: comicShadow,
              cursor: 'pointer',
            }}
          >
            <FaShoppingCart />
            Comprar ahora
          </button>

          <div className="mt-16 animate-bounce">
            <FaChevronDown className="text-2xl mx-auto" style={{ color: `${C.paper}88` }} />
          </div>
        </div>
      </section>

      {/* ════════════ NAV STICKY ════════════ */}
      <nav
        className="sticky top-[72px] z-30 backdrop-blur"
        style={{ backgroundColor: `${C.deep}ee`, borderBottom: `2px solid ${C.ink}` }}
      >
        <div className="container mx-auto px-4 flex gap-1 overflow-x-auto py-2 scrollbar-hide">
          {GAME_SECTIONS.map(s => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="whitespace-nowrap px-4 py-2 rounded-lg text-sm font-bold transition-all"
              style={{
                backgroundColor: activeSection === s.id ? C.sand : 'transparent',
                color: activeSection === s.id ? C.ink : C.paper,
                border: activeSection === s.id ? `2px solid ${C.ink}` : '2px solid transparent',
              }}
            >
              {s.label}
            </a>
          ))}
        </div>
      </nav>

      {/* ════════════ CONCEPTO ════════════ */}
      <section id="concepto" className="py-20 md:py-28" style={{ backgroundColor: C.paper }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2
              className="text-3xl md:text-5xl font-extrabold mb-8 text-center"
              style={{ color: C.deep }}
            >
              ¿De qué se trata?
            </h2>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div
                className="rounded-2xl p-8 transition-all hover:translate-y-[-4px]"
                style={{ backgroundColor: '#fff', border: comicBorder, boxShadow: comicShadow }}
              >
                <div className="text-4xl mb-4">🏢</div>
                <h3 className="text-xl font-extrabold mb-3" style={{ color: C.teal }}>
                  La oficina más tóxica del reino animal
                </h3>
                <p className="leading-relaxed" style={{ color: C.ink }}>
                  En <strong style={{ color: C.deep }}>Ahí les dejo</strong> cada jugador es un empleado
                  tratando de sobrevivir al caos laboral. El objetivo es ser el último
                  trabajador activo sin llegar a 10 o más puntos de estrés.
                </p>
              </div>

              <div
                className="rounded-2xl p-8 transition-all hover:translate-y-[-4px]"
                style={{ backgroundColor: '#fff', border: comicBorder, boxShadow: comicShadow }}
              >
                <div className="text-4xl mb-4">🃏</div>
                <h3 className="text-xl font-extrabold mb-3" style={{ color: C.teal }}>
                  130 cartas + 24 tokens
                </h3>
                <p className="leading-relaxed" style={{ color: C.ink }}>
                  El juego incluye 60 Cartas de Trabajo y 70 Cartas de Acción,
                  para un total de 130 cartas. Además, se usan
                  24 tokens de estrés para llevar el desgaste de cada empleado durante la partida.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-12">
              {[
                { title: 'Mazo de Trabajo', value: '60 cartas', detail: 'Tareas e imprevistos que aumentan estrés.' },
                { title: 'Mazo de Acción', value: '70 cartas', detail: 'Efectos para reducir, mover o subir estrés.' },
                { title: 'Total del juego', value: '130 cartas', detail: 'Contenido total del juego base.' },
              ].map((item, i) => (
                <div
                  key={i}
                  className="rounded-2xl p-5"
                  style={{ backgroundColor: '#fff', border: comicBorder, boxShadow: comicShadowSm }}
                >
                  <p className="text-sm font-bold mb-1" style={{ color: C.teal }}>{item.title}</p>
                  <p className="text-2xl font-extrabold mb-1" style={{ color: C.deep }}>{item.value}</p>
                  <p className="text-sm" style={{ color: C.ink }}>{item.detail}</p>
                </div>
              ))}
            </div>

            <div
              className="rounded-2xl p-8 text-center"
              style={{
                background: `linear-gradient(135deg, ${C.teal}, ${C.deep})`,
                border: comicBorder,
                boxShadow: comicShadow,
              }}
            >
              <p className="text-2xl font-extrabold mb-2" style={{ color: C.sand }}>
                &ldquo;El mejor team building es reírse de la toxicidad laboral&rdquo;
              </p>
              <p style={{ color: `${C.paper}cc` }}>
                — Ideal para noches de juegos, despedidas de trabajo, o para jugarlo
                en la oficina cuando el jefe no está mirando.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════ COMPONENTES ════════════ */}
      <section
        id="componentes"
        className="py-20 md:py-28"
        style={{ background: `linear-gradient(180deg, ${C.teal} 0%, ${C.deep} 100%)` }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2
              className="text-3xl md:text-5xl font-extrabold mb-4 text-center"
              style={{ color: C.sand }}
            >
              Componentes del juego
            </h2>
            <p className="text-center text-lg mb-12 max-w-2xl mx-auto" style={{ color: `${C.paper}cc` }}>
              Esta es la estructura oficial de Ahí les dejo para cada partida.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {GAME_COMPONENTS.map((item, i) => (
                <div
                  key={i}
                  className="group rounded-2xl p-5 text-center transition-all hover:translate-y-[-6px] cursor-default"
                  style={{
                    backgroundColor: C.paper,
                    border: comicBorder,
                    boxShadow: comicShadow,
                  }}
                >
                  <div
                    className="text-4xl md:text-5xl mb-3 group-hover:scale-110 transition-transform"
                    style={{ color: C.teal }}
                  >
                    {item.icon}
                  </div>
                  <h3 className="font-extrabold text-sm md:text-base mb-1" style={{ color: C.ink }}>
                    {item.title}
                  </h3>
                  <p className="text-sm md:text-base font-extrabold" style={{ color: C.deep }}>{item.value}</p>
                  <p className="text-xs md:text-sm mt-1" style={{ color: C.deep }}>{item.detail}</p>
                </div>
              ))}
            </div>

            <p className="text-center mt-8 text-sm" style={{ color: `${C.paper}99` }}>
              Objetivo: ser el último empleado activo sin alcanzar 10 o más de estrés.
            </p>
          </div>
        </div>
      </section>

      {/* ════════════ CARTAS ════════════ */}
      <section id="cartas" className="py-20 md:py-28" style={{ backgroundColor: C.paper }}>
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2
              className="text-3xl md:text-5xl font-extrabold mb-4 text-center"
              style={{ color: C.deep }}
            >
              Las cartas
            </h2>
            <p className="text-center text-lg mb-12 max-w-2xl mx-auto" style={{ color: C.ink }}>
              Mazo de Trabajo: 60 cartas. Mazo de Acción: 70 cartas.
              En total son 130 cartas dentro del juego.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {CARD_EXAMPLES.map((card, i) => (
                <div
                  key={i}
                  className="rounded-2xl p-4 transition-all hover:translate-y-[-6px] hover:scale-[1.02]"
                  style={{
                    backgroundColor: '#fff',
                    border: comicBorder,
                    boxShadow: comicShadow,
                    borderTop: `6px solid ${card.accent}`,
                  }}
                >
                  <img
                    src={card.image}
                    alt={card.title}
                    className="w-full h-auto rounded-xl mb-4"
                    style={{ border: `2px solid ${C.ink}` }}
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════ CÓMO SE JUEGA ════════════ */}
      <section
        id="como-jugar"
        className="py-20 md:py-28"
        style={{ backgroundColor: C.deep }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2
              className="text-3xl md:text-5xl font-extrabold mb-12 text-center"
              style={{ color: C.sand }}
            >
              ¿Cómo se juega?
            </h2>

            <div
              className="rounded-2xl p-6 mb-8"
              style={{ backgroundColor: C.paper, border: comicBorder, boxShadow: comicShadow }}
            >
              <h3 className="text-xl font-extrabold mb-3" style={{ color: C.deep }}>Reglas clave</h3>
              <ul className="space-y-2 text-sm md:text-base" style={{ color: C.ink }}>
                <li>• Juegan de 3 a 8 empleados por partida.</li>
                <li>• Cada turno tiene 3 fases: Trabajo, Acción y Cierre.</li>
                <li>• Si llegas a 10 o más de estrés, renuncias: “¡Ahí les dejo!”.</li>
                <li>• Puedes gestionar estrés con tokens o descartando cartas de Trabajo.</li>
                <li>• Gana el último empleado activo sin haber colapsado.</li>
              </ul>
            </div>

            <div className="space-y-6">
              {[
                {
                  step: '01',
                  title: 'Fase de Trabajo',
                  desc: 'En tu turno robas 1 Carta de Trabajo y resuelves su efecto de inmediato. Estas cartas aumentan el estrés del empleado según el valor indicado.',
                },
                {
                  step: '02',
                  title: 'Fase de Acción',
                  desc: 'Elige una opción: robar 1 Carta de Acción o jugar 1 Carta de Acción de tu mano. Estas cartas permiten reducir tu estrés, subir el de otros o anular efectos.',
                },
                {
                  step: '03',
                  title: 'Fase de Cierre',
                  desc: 'Si llegas a 10 o más de estrés, renuncias inmediatamente diciendo: “¡Ahí les dejo!”. Luego repartes tu carta de trabajo con mayor estrés al empleado con menor estrés y sales de la partida.',
                },
                {
                  step: '04',
                  title: 'Condición de victoria',
                  desc: 'Gana el último empleado activo que no haya alcanzado 10 puntos de estrés. Los tokens de estrés y las cartas de trabajo/acción se combinan para gestionar ese límite.',
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex gap-5 items-start rounded-2xl p-6 transition-all hover:translate-x-2"
                  style={{ backgroundColor: C.paper, border: comicBorder, boxShadow: comicShadow }}
                >
                  <div
                    className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
                    style={{
                      background: `linear-gradient(135deg, ${C.teal}, ${C.cyan})`,
                      border: comicBorder,
                    }}
                  >
                    <span className="font-extrabold text-lg" style={{ color: C.ink }}>{item.step}</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-extrabold mb-2" style={{ color: C.deep }}>{item.title}</h3>
                    <p className="leading-relaxed" style={{ color: C.ink }}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════ COMPRAR ════════════ */}
      <section id="comprar" className="py-20 md:py-28" style={{ backgroundColor: C.paper }}>
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <span
              className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-extrabold rounded-full mb-6"
              style={{ backgroundColor: C.mustard, color: C.ink, border: `2px solid ${C.ink}` }}
            >
              <FaFire className="animate-pulse" />
              ¡DISPONIBLE AHORA!
            </span>
            <h2
              className="text-3xl md:text-5xl font-extrabold mb-4"
              style={{ color: C.deep }}
            >
              Compra tu copia
            </h2>
            <p className="text-lg mb-10" style={{ color: C.ink }}>
              <strong style={{ color: C.teal }}>Ahí les dejo</strong> ya está disponible.
              Consigue tu copia y empieza a renunciar con estilo.
            </p>

            <div
              className="rounded-2xl p-8 mb-8"
              style={{ backgroundColor: '#fff', border: comicBorder, boxShadow: comicShadow }}
            >
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="text-left">
                  <p className="text-sm font-bold mb-1" style={{ color: C.teal }}>Juego de cartas</p>
                  <p className="text-3xl font-extrabold" style={{ color: C.deep }}>Ahí les dejo</p>
                  <p className="text-sm mt-1" style={{ color: C.ink }}>130 cartas · 24 tokens de estrés · 3–8 jugadores</p>
                </div>
                <button
                  onClick={handleBuyNow}
                  className="inline-flex items-center gap-2 font-extrabold py-4 px-8 rounded-xl text-lg transition-all transform hover:scale-105 hover:translate-y-[-2px] whitespace-nowrap"
                  style={{
                    backgroundColor: C.teal,
                    color: C.paper,
                    border: comicBorder,
                    boxShadow: comicShadowSm,
                    cursor: 'pointer',
                  }}
                >
                  <FaShoppingCart />
                  Comprar ahora
                </button>
              </div>
            </div>

            <p className="text-sm" style={{ color: C.deep }}>
              Envíos a todo Colombia. Pago seguro con múltiples métodos.
            </p>
          </div>
        </div>
      </section>

      {/* ════════════ FAQ RÁPIDO ════════════ */}
      <section
        className="py-16"
        style={{ background: `linear-gradient(180deg, ${C.teal} 0%, ${C.deep} 100%)` }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2
              className="text-2xl font-extrabold mb-8 text-center"
              style={{ color: C.sand }}
            >
              Preguntas frecuentes
            </h2>
            <div className="space-y-4">
              {[
                { q: '¿Dónde puedo comprar el juego?', a: '¡Ya está disponible en nuestra tienda! Puedes comprarlo directamente en tablero-infinito.com con envío a todo Colombia.' },
                { q: '¿Cuántas personas pueden jugar?', a: 'Se juega de 3 a 8 empleados por partida.' },
                { q: '¿Es apropiado para jugar en la oficina?', a: 'Técnicamente sí… si tu jefe tiene sentido del humor. Recomendado para after office, team buildings, o noches de juegos con amigos.' },
                { q: '¿Cómo se reparten las cartas y tokens?', a: 'El juego incluye 60 Cartas de Trabajo, 70 Cartas de Acción y 24 tokens de estrés para gestionar los efectos durante la partida.' },
                { q: '¿Hacen envíos a todo Colombia?', a: 'Sí, realizamos envíos a nivel nacional.' },
              ].map((faq, i) => (
                <details
                  key={i}
                  className="group rounded-xl overflow-hidden"
                  style={{ backgroundColor: C.paper, border: comicBorder, boxShadow: comicShadowSm }}
                >
                  <summary
                    className="flex justify-between items-center cursor-pointer p-5 font-bold"
                    style={{ color: C.ink }}
                  >
                    {faq.q}
                    <FaChevronDown
                      className="group-open:rotate-180 transition-transform flex-shrink-0 ml-4"
                      style={{ color: C.teal }}
                    />
                  </summary>
                  <p className="px-5 pb-5 leading-relaxed" style={{ color: C.deep }}>{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════ CTA FINAL ════════════ */}
      <section className="py-20" style={{ backgroundColor: C.paper }}>
        <div className="container mx-auto px-4 text-center">
          <p className="text-5xl mb-6">🃏</p>
          <h2
            className="text-3xl md:text-4xl font-extrabold mb-4"
            style={{ color: C.deep }}
          >
            ¿Listo para renunciar con estilo?
          </h2>
          <p className="text-lg mb-8 max-w-lg mx-auto" style={{ color: C.ink }}>
            Ahí les dejo — 130 cartas + 24 tokens de estrés en una oficina donde solo sobrevive uno.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={handleBuyNow}
              className="inline-flex items-center justify-center gap-2 font-extrabold py-4 px-10 rounded-xl text-lg transition-all transform hover:scale-105 hover:translate-y-[-2px]"
              style={{
                backgroundColor: C.sand,
                color: C.ink,
                border: comicBorder,
                boxShadow: comicShadow,
                cursor: 'pointer',
              }}
            >
              <FaShoppingCart />
              Comprar ahora
            </button>
            <Link
              to="/productos"
              className="inline-flex items-center justify-center gap-2 font-bold py-4 px-10 rounded-xl text-lg transition-all hover:translate-y-[-2px]"
              style={{
                backgroundColor: C.paper,
                color: C.deep,
                border: comicBorder,
                boxShadow: comicShadowSm,
              }}
            >
              Ver tienda completa
            </Link>
          </div>
        </div>
      </section>

      {/* ════════════ VOLVER ARRIBA ════════════ */}
      <div className="pb-8 text-center" style={{ backgroundColor: C.paper }}>
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="inline-flex items-center transition-colors group text-sm font-bold hover:opacity-80"
          style={{ color: C.teal }}
        >
          <span className="mr-2">Volver arriba</span>
          <FaArrowUp className="w-4 h-4 group-hover:-translate-y-1 transition-transform" />
        </button>
      </div>
    </div>
  );
};

export default AhiLesDejoPage;
